import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Link } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import { getThemeTokens } from '../../../theme/tokens';
import { EntityPanelFrame } from './EntityPanelFrame';
import { Sparkline } from './Sparkline';
import {
  VANTAGE_REPORT_TOKEN_ANNOTATION,
  VantageReportNotFoundError,
  useVantageCost,
} from './useVantageCost';

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Vantage icon mark extracted from https://www.vantage.sh/logo.svg (first 24×24 of the viewBox)
const VantageIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    style={{ display: 'block', flexShrink: 0 }}
    aria-hidden
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.28508 2.02236C7.24068 0.703789 9.53982 4.5488e-07 11.8918 4.5488e-07C13.4536 -0.000433521 15.0002 0.309661 16.4431 0.91256C17.8861 1.51547 19.1972 2.39936 20.3016 3.51375C21.406 4.62813 22.2819 5.95118 22.8793 7.40729C23.4768 8.86339 23.7841 10.424 23.7836 12C23.7836 14.3733 23.0862 16.6934 21.7795 18.6668C20.4728 20.6403 18.6156 22.1783 16.4426 23.0866C14.2697 23.9948 11.8786 24.2324 9.57182 23.7694C7.26505 23.3064 5.14614 22.1635 3.48304 20.4852C1.81994 18.807 0.687352 16.6689 0.228504 14.3411C-0.230345 12.0133 0.00515151 9.60053 0.905218 7.40781C1.80528 5.21509 3.32948 3.34094 5.28508 2.02236ZM3.34202 10.3716L6.90989 13.9752C6.94532 14.0123 6.98776 14.0418 7.03472 14.0619C7.08168 14.082 7.13218 14.0924 7.1832 14.0924C7.23422 14.0924 7.28474 14.082 7.3317 14.0619C7.37867 14.0418 7.42111 14.0123 7.45652 13.9752L11.0179 10.3716C11.089 10.2997 11.129 10.2023 11.129 10.1007C11.129 9.9992 11.089 9.90178 11.0179 9.82987L7.4467 6.22956C7.37549 6.15776 7.27897 6.11742 7.17831 6.11742C7.07764 6.11742 6.98109 6.15776 6.90989 6.22956L3.34202 9.82987C3.27085 9.90178 3.2309 9.9992 3.2309 10.1007C3.2309 10.2023 3.27085 10.2997 3.34202 10.3716ZM12.1603 18.7283L15.7281 15.128C15.7993 15.0562 15.8392 14.9588 15.8392 14.8572C15.8392 14.7556 15.7993 14.6581 15.7281 14.5863L12.1603 10.9827C12.0891 10.9108 11.9924 10.8705 11.8918 10.8705C11.7911 10.8705 11.6946 10.9108 11.6234 10.9827L8.05227 14.5863C7.98111 14.6581 7.94113 14.7556 7.94113 14.8572C7.94113 14.9588 7.98111 15.0562 8.05227 15.128L11.6234 18.7283C11.6946 18.8001 11.7911 18.8404 11.8918 18.8404C11.9924 18.8404 12.0891 18.8001 12.1603 18.7283ZM16.8705 13.9752L20.4383 10.3716C20.474 10.3363 20.5023 10.294 20.5216 10.2476C20.5409 10.2011 20.5508 10.1512 20.5508 10.1007C20.5508 10.0503 20.5409 10.0004 20.5216 9.95387C20.5023 9.90738 20.474 9.86525 20.4383 9.82987L16.8705 6.22956C16.7993 6.15776 16.7028 6.11742 16.602 6.11742C16.5014 6.11742 16.4049 6.15776 16.3337 6.22956L12.7625 9.82987C12.6914 9.90178 12.6514 9.9992 12.6514 10.1007C12.6514 10.2023 12.6914 10.2997 12.7625 10.3716L16.3337 13.9752C16.4049 14.047 16.5014 14.0874 16.602 14.0874C16.7028 14.0874 16.7993 14.047 16.8705 13.9752Z"
      fill="#6742D6"
    />
  </svg>
);

function TrendDelta({
  current,
  previous,
  currency,
}: {
  current: number;
  previous: number;
  currency: string;
}) {
  const theme = useTheme();

  if (previous === 0) return null;

  const delta = current - previous;
  const pct = Math.round((delta / previous) * 100);
  const isUp = delta > 0;

  return (
    <Typography
      component="span"
      sx={{
        color: isUp ? theme.palette.error.main : theme.palette.success.main,
        fontSize: 14,
        fontWeight: 700,
        textAlign: 'right',
      }}
    >
      {isUp ? '▲' : '▼'} {formatCurrency(Math.abs(delta), currency)} (
      {Math.abs(pct)}%) vs prior period
    </Typography>
  );
}

export const EntityTeamCostCard = () => {
  const { entity } = useEntity();
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  const hasAnnotation = Boolean(
    entity.metadata.annotations?.[VANTAGE_REPORT_TOKEN_ANNOTATION],
  );

  const { loading, error, data } = useVantageCost(entity, 30);

  if (!hasAnnotation) return null;

  if (loading) {
    return (
      <EntityPanelFrame title="Cloud cost">
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
        <Skeleton variant="text" sx={{ mt: 1.5, width: '60%' }} />
      </EntityPanelFrame>
    );
  }

  if (error || !data) {
    const isNotFound = error instanceof VantageReportNotFoundError;
    return (
      <EntityPanelFrame title="Cloud cost">
        <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>
          {isNotFound ? (
            <>
              Report not found. Update the <code>vantage.sh/report-token</code>{' '}
              annotation with a valid token.
            </>
          ) : error ? (
            'Could not load cost data.'
          ) : (
            'No cost data available'
          )}
        </Typography>
      </EntityPanelFrame>
    );
  }

  const { total, previousTotal, trend, periodStart, periodEnd, reportUrl } =
    data;
  const accentColor = theme.palette.primary.main;

  const windowDays =
    Math.round(
      (new Date(periodEnd).getTime() - new Date(periodStart).getTime()) /
        86_400_000,
    ) + 1;
  const periodLabel = `Last ${windowDays} days`;

  return (
    <EntityPanelFrame
      title="Cloud cost"
      action={
        <Link to={reportUrl} target="_blank" rel="noopener noreferrer">
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
            <VantageIcon size={14} />
            <Typography
              sx={{ color: accentColor, fontSize: 12, fontWeight: 700 }}
            >
              View report ↗
            </Typography>
          </Box>
        </Link>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {periodLabel}
        </Typography>

        {/* Total (left) · delta (right) */}
        <Box
          sx={{
            alignItems: 'flex-end',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: tokens.textPrimary,
              fontSize: 32,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {formatCurrency(total.amount, total.currency)}
          </Typography>
          {previousTotal !== undefined && (
            <TrendDelta
              current={total.amount}
              previous={previousTotal}
              currency={total.currency}
            />
          )}
        </Box>

        {trend.length >= 2 && (
          <Box
            sx={{
              backgroundColor: alpha(accentColor, 0.06),
              borderRadius: 1,
              p: 1,
            }}
          >
            <Sparkline
              points={trend.map(p => ({ value: p.amount }))}
              color={accentColor}
            />
          </Box>
        )}
      </Box>
    </EntityPanelFrame>
  );
};
